import {
  Dialog,
  IconButton,
  makeStyles,
  Typography,
  CircularProgress,
} from '@material-ui/core'
import React, { useEffect, useRef, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { useAppName, useAppLogo } from 'commons/usePartner'
import { all } from 'bluebird'
import * as R from 'ramda'
import CloseIcon from '@material-ui/icons/Close'
import OutlinedButtons from 'app/componentV4/Button/OutlinedButtons'
import ContainedButton from 'app/componentV4/Button/ContainedButton'
import { CLOSE, SECONDARY } from 'app/constants/button'

const fragmentText = (ctx, text, maxWidth) => {
  const words = text.split(' ')
  const lines = []
  let line = ''
  if (ctx.measureText(text).width < maxWidth) {
    return [text]
  }
  while (words.length > 0) {
    let split = false
    while (ctx.measureText(words[0]).width >= maxWidth) {
      const tmp = words[0]
      words[0] = tmp.slice(0, -1)
      if (!split) {
        split = true
        words.splice(1, 0, tmp.slice(-1))
      } else {
        words[1] = tmp.slice(-1) + words[1]
      }
    }
    if (ctx.measureText(line + words[0]).width < maxWidth) {
      line += words.shift() + ' '
    } else {
      lines.push(line)
      line = ''
    }
    if (words.length === 0) {
      lines.push(line)
    }
  }
  return lines
}

function createBanner(
  canvas,
  font,
  placeName,
  backgroundImageURL,
  qrCodeImageURL,
  placeLogoImageURL,
  appLogoImageURL,
) {
  return all(
    [backgroundImageURL, qrCodeImageURL, placeLogoImageURL, appLogoImageURL]
      .filter(R.complement(R.isNil))
      .map(loadImage),
  ).then(([backgroundImage, qrCodeImage, placeLogoImage, appLogoImage]) => {
    const ctx = canvas.getContext('2d')
    ctx.fillStyle = 'rgb(255, 255, 255)'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    ctx.drawImage(
      backgroundImage,
      0,
      0 * canvas.height,
      canvas.width,
      0.9375 * canvas.height,
    )

    ctx.font = font
    ctx.fillStyle = '#183560'
    ctx.textAlign = 'center'
    const lines = fragmentText(ctx, placeName, 0.87 * canvas.width)

    lines.forEach((line, i) =>
      ctx.fillText(
        line,
        0.5 * canvas.width,
        i * 1 * canvas.height + 0.045 * canvas.height,
      ),
    )

    if (placeLogoImage) {
      const ratio = placeLogoImage.naturalWidth / placeLogoImage.naturalHeight
      // const heightLogo = 0.2391 * canvas.height
      const heightLogo = 0.11 * canvas.height

      ctx.drawImage(
        placeLogoImage,
        (canvas.width - heightLogo * ratio) / 2,
        0.08 * canvas.height,
        heightLogo * ratio,
        heightLogo,
      )
    }

    if (qrCodeImage) {
      const ratio = qrCodeImage.naturalWidth / qrCodeImage.naturalHeight
      // const heightLogo = 0.2391 * canvas.height
      const heightLogo = 0.4 * canvas.height

      ctx.drawImage(
        qrCodeImage,
        (canvas.width - heightLogo * ratio) / 2,
        0.31 * canvas.height,
        heightLogo * ratio,
        heightLogo,
      )
    }

    if (appLogoImage) {
      const ratio = appLogoImage.naturalWidth / appLogoImage.naturalHeight
      const maxWidth = canvas.width * 0.5
      const maxHeight = canvas.height * 0.2 * 0.6
      const maxRatio = maxWidth / maxHeight
      const isImageRatioWiderThanAvailableSpace = ratio > maxRatio
      // Use maxWidth when the appLogo has wider ratio compared to available space (maxWidth),
      // then the height calculated from `maxWidth / ratio` will never longer than maxHeight.
      // on the otherhand, use maxHeight when the appLogo has taller ratio.
      const width = isImageRatioWiderThanAvailableSpace
        ? maxWidth
        : maxHeight * ratio
      const height = isImageRatioWiderThanAvailableSpace
        ? maxWidth / ratio
        : maxHeight

      ctx.drawImage(
        appLogoImage,
        (canvas.width - width) / 2,
        0.88 * canvas.height - height / 4,
        width,
        height,
      )
    }

    ctx.save()
    return canvas
  })
}

function loadImage(src) {
  return new Promise((resolve) => {
    const image = new Image()
    image.src = src
    image.crossOrigin = 'anonymous'
    image.onload = function () {
      resolve(image)
    }
  })
}

function canvasToBlob(fileType) {
  return (canvas) =>
    new Promise((resolve) => {
      canvas.toBlob(function (blob) {
        resolve(blob)
      }, fileType)
    })
}

function startDownloading(url, downloadFilename) {
  const link = document.createElement('a')
  link.href = url
  link.download = downloadFilename
  link.click()
}

const PrintableStatus = {
  loading: 'loading',
  completed: 'completed',
  failed: 'failed',
}

const useStyle = makeStyles((theme) => ({
  qrButton: {
    width: 'max-content',
    color: '#00AFF0',
    '& svg': {
      fill: '#00AFF0',
      margin: '0px 3px',
    },
  },
  qrIcon: {
    fill: '#00AFF0',
    width: 30,
    marginRight: 8,
  },
  classClose: {
    cursor: 'pointer',
  },
  loaderContainer: {
    height: 500,
    overflow: 'auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  imageContainer: {
    height: 500,
    overflow: 'auto',
    position: 'relative',
  },
  canvasBackground: {
    position: 'absolute',
    top: 50,
    left: 0,
    width: 528,
    height: 747,
    zIndex: 1,
    backgroundSize: 'contain',
  },
  canvasPreview: {
    position: 'relative',
    zIndex: 2,
  },
  qrButtonText: {
    color: '#00AFF0',
    fontWeight: '400',
    fontSize: '14px',
  },
}))

export default ({
  open,
  onClose,
  placeName,
  backgroundImageURL,
  placeLogoImageURL,
  qrCodeImageURL,
}) => {
  const previewCanvasRef = useRef(null)
  const printableCanvasRef = useRef(null)
  const appName = useAppName()
  const { banner: appLogoImageURL } = useAppLogo()
  const [isPreviewLoading, setPreviewLoading] = useState(false)
  const [printableStatus, setPrintableStatus] = useState({ status: undefined })
  const classes = useStyle()
  const isPrintableLoading = printableStatus.status === PrintableStatus.loading

  useEffect(() => {
    if (!previewCanvasRef.current) {
      return
    }
    setPreviewLoading(true)
    createBanner(
      previewCanvasRef.current,
      'bold 16px Arial',
      placeName,
      backgroundImageURL,
      qrCodeImageURL,
      placeLogoImageURL,
      appLogoImageURL,
    ).then(() => {
      setPreviewLoading(false)
    })
  }, [
    previewCanvasRef,
    setPreviewLoading,
    placeName,
    backgroundImageURL,
    qrCodeImageURL,
    placeLogoImageURL,
    appLogoImageURL,
  ])

  function downloadPrintableBanner() {
    function download(printableURL) {
      startDownloading(printableURL, `${appName}_QR_Code.png`)
    }

    if (printableStatus.status === PrintableStatus.completed) {
      download(printableStatus.printableURL)
      return
    }
    if (
      !printableCanvasRef.current ||
      R.includes(printableStatus.status, [
        PrintableStatus.loading,
        PrintableStatus.completed,
      ])
    ) {
      return
    }

    setPrintableStatus({ status: PrintableStatus.loading })
    createBanner(
      printableCanvasRef.current,
      'bold 36px Arial',
      placeName,
      backgroundImageURL,
      qrCodeImageURL,
      placeLogoImageURL,
      appLogoImageURL,
    )
      .then(canvasToBlob('image/png'))
      .then((blob) => {
        const printableURL = URL.createObjectURL(blob)
        setPrintableStatus({ status: PrintableStatus.completed, printableURL })
        download(printableURL)
      })
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        style: { borderRadius: 16, padding: '3rem' },
      }}
    >
      <div className="flex justify-between">
        <Typography className="text-16 font-500 text-primary items-center flex">
          <FormattedMessage id="qr.button" />
        </Typography>
        <IconButton aria-label="close-opqr-code-dialog" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>

      <div className="relative flex flex-col">
        <Typography className="text-14 text-primary items-center flex">
          <FormattedMessage id="qr.text" />
        </Typography>
        <canvas
          className="border-1 border-primary-350 my-10 rounded-12"
          ref={previewCanvasRef}
          width={600}
          height={879}
        />
        {/* </div> */}
        <canvas
          className="hidden"
          ref={printableCanvasRef}
          width={1200}
          height={1758}
        />
        {isPreviewLoading && (
          <div className="absolute inset-0 flex w-full h-full items-center justify-center">
            <CircularProgress size={48} color="secondary" />
          </div>
        )}
      </div>
      <div className="flex justify-center space-x-5 mt-10">
        <OutlinedButtons color={CLOSE} onClick={onClose}>
          <FormattedMessage id="text.close" />
        </OutlinedButtons>
        <ContainedButton
          type="submit"
          color={SECONDARY}
          onClick={() => downloadPrintableBanner()}
        >
          <FormattedMessage id="qr.download" />
        </ContainedButton>
      </div>
    </Dialog>
  )
}
